import Chip from '@liquidweb-dev/undercurrent/Chip';
import Stack, { type StackProps } from '@liquidweb-dev/undercurrent/Stack';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';

export interface PaymentMethodBadgesProps extends StackProps {
	isBackup?: boolean;
	isDefault?: boolean;
	isExpired?: boolean;
}

/**
 * The Default, Backup and Expired badges a payment method carries, in that order,
 * or nothing when it carries none
 */
const PaymentMethodBadges = ({
	isBackup,
	isDefault,
	isExpired,
	sx,
	...rest
}: PaymentMethodBadgesProps) => {
	if (!isDefault && !isBackup && !isExpired) return null;

	return (
		<Stack
			sx={combineSx(
				{ alignItems: 'center', flexDirection: 'row', flexShrink: 0, gap: 150 },
				sx,
			)}
			{...rest}
		>
			{isDefault && <Chip color="highlight" label="Default" size="small" />}
			{isBackup && (
				/*
				 * `color="default"` fills with `bg.main` (#ffffff), so the chip
				 * disappears against the card. The design fills it with `bg.weak`
				 * (#f6f5f4) — sampled from Figma `3875-17056` — which no `color`
				 * value maps to, hence the override. Its border is already
				 * `border.main` from the Chip root and needs nothing.
				 */
				<Chip
					color="default"
					label="Backup"
					size="small"
					sx={({ vars }) => ({
						backgroundColor: vars.palette.uc.bg.weak,
					})}
				/>
			)}
			{isExpired && <Chip color="danger" label="Expired" size="small" />}
		</Stack>
	);
};

export default PaymentMethodBadges;
